import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { HandymanSpecialtySelection } from "@/lib/handymanConfig";

// ---------------------------------------------------------------------------
// Lecture : sous-spécialités d'un artisan donné (par artisan_profiles.id)
// ---------------------------------------------------------------------------

export interface HandymanSpecialtyRow {
  artisan_id: string;
  specialty_code: string;
  other_specialty_label: string | null;
  created_at: string;
}

export function useHandymanSpecialties(artisanProfileId: string | undefined) {
  return useQuery<HandymanSpecialtyRow[]>({
    queryKey: ["handyman-specialties", artisanProfileId],
    queryFn: async () => {
      if (!artisanProfileId) return [];
      const { data, error } = await supabase
        .from("artisan_handyman_specialties")
        .select("*")
        .eq("artisan_id", artisanProfileId)
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as HandymanSpecialtyRow[];
    },
    enabled: !!artisanProfileId,
  });
}

// ---------------------------------------------------------------------------
// Mutation : remplace toutes les spécialités (DELETE puis INSERT)
// ---------------------------------------------------------------------------

export function useUpsertHandymanSpecialties(artisanProfileId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (selections: HandymanSpecialtySelection[]) => {
      if (!artisanProfileId) throw new Error("artisanProfileId requis");

      // 1. Supprimer les anciennes
      const { error: deleteError } = await supabase
        .from("artisan_handyman_specialties")
        .delete()
        .eq("artisan_id", artisanProfileId);
      if (deleteError) throw deleteError;

      // 2. Insérer les nouvelles (si non vide)
      if (selections.length === 0) return;

      const rows = selections.map((s) => ({
        artisan_id: artisanProfileId,
        specialty_code: s.specialty_code,
        other_specialty_label: s.specialty_code === "other"
          ? (s.other_specialty_label ?? null)
          : null,
      }));

      const { error: insertError } = await supabase
        .from("artisan_handyman_specialties")
        .insert(rows);
      if (insertError) throw insertError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["handyman-specialties", artisanProfileId],
      });
    },
  });
}
